import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Home } from 'lucide-react';
import {
  Alert,
  Heading,
  Button,
  MetricCard,
  MetricCardGrid,
  Card,
  DataGrid,
  FilterBar,
  Input,
  Select,
  METRIC_READ_CAPTIONS,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import { propertyValuationsApi } from '../../api/services';
import type { components } from '@aaimplatform/institutional-types/property-title-admin';
import {
  Absent,
  EnumBadge,
  GridEmpty,
  GridSkeleton,
  ListHeader,
  PROPERTY_TYPE_LABELS,
  VALUATION_STATUS,
  filterOptions,
  formatUsd,
  formatUsdCompact,
  labelFor,
  meanReported,
  reportedNumber,
  reportedPropertyType,
  sumReported,
  tile,
  type ListReadState,
} from './CollateralListParts';

type PropertyValuation = components['schemas']['PropertyValuation'];
type ValuationStatus = components['schemas']['ValuationStatus'];

const STATUS_FILTER: FilterDefinition = {
  id: 'status',
  label: 'Status',
  type: 'select',
  options: filterOptions(VALUATION_STATUS),
};

// The list request asks for the most rows listPropertyValuations allows; the
// service's window is the same 100 newest valuations.
const LIST_LIMIT = 100;

/** The label a row's reported property type reads as, or null when it reports none. */
function typeLabel(valuation: PropertyValuation): string | null {
  const type = reportedPropertyType(valuation.propertyType);
  return type == null ? null : labelFor(PROPERTY_TYPE_LABELS, type);
}

function isValuationStatus(v: string | undefined): v is ValuationStatus {
  return v != null && Object.prototype.hasOwnProperty.call(VALUATION_STATUS, v);
}

// ZIP plus street: the profile page opens the stored profile whose street
// matches, and shows the ZIP list only when none does. PropertyValuation
// carries no attomId to link by.
function profileHref(valuation: PropertyValuation): string | null {
  if (!valuation.address?.zipCode) return null;
  const params = new URLSearchParams({ zipCode: valuation.address.zipCode });
  if (valuation.address.street) params.set('street', valuation.address.street);
  return `/collateral/property-profile?${params.toString()}`;
}

function RightHeader({ children }: { children: string }) {
  return <span className="block w-full text-right">{children}</span>;
}

/**
 * Property Valuations Page
 *
 * Lists property valuation records with a status filter, a property type
 * filter, an address search, and the New Valuation form. A row opens the
 * stored property profile for its address.
 *
 * The status filter reads on the server (listPropertyValuations `status`).
 * The property type filter and the search apply to the loaded rows: the spec
 * declares no property type parameter. The tiles count the whole window, never
 * only the rows a status selects.
 *
 * The money column is labeled 'Estimated value': the service returns the AVM
 * estimate in PropertyValuation.certifiedValue whatever the row's status, so
 * only a CERTIFIED row's figure counts toward the Certified value tile.
 * Confidence is shown as reported, with a neutral bar: the status badge is
 * the one tier the page shows, and it comes from the service.
 *
 * Wired to canonical /api/v1/property/valuations endpoints via the
 * property-title-admin typed client.
 */
export function PropertyValuationsPage() {
  const navigate = useNavigate();
  // `book` is the unfiltered window the tiles count; `valuations` is what the
  // server returned for the chosen status, the rows the table shows.
  const [book, setBook] = useState<PropertyValuation[]>([]);
  const [valuations, setValuations] = useState<PropertyValuation[]>([]);
  const [read, setRead] = useState<ListReadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const statusValue = activeFilters.find((f) => f.filterId === 'status')?.value;
  const statusFilter = isValuationStatus(statusValue) ? statusValue : undefined;
  const typeFilter = activeFilters.find((f) => f.filterId === 'propertyType')?.value || undefined;

  const fetchValuations = useCallback(async () => {
    setRead('loading');
    setError(null);
    try {
      const [all, selected] = await Promise.all([
        propertyValuationsApi.list({ limit: LIST_LIMIT }),
        statusFilter
          ? propertyValuationsApi.list({ status: statusFilter, limit: LIST_LIMIT })
          : null,
      ]);
      setBook(all.valuations);
      setValuations(selected ? selected.valuations : all.valuations);
      setRead('loaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load property valuations');
      setRead('failed');
    }
  }, [statusFilter]);

  useEffect(() => {
    void fetchValuations();
  }, [fetchValuations]);

  // Create form state -- mirrors the canonical PropertyValuationRequest body:
  // { address: { street, city, state, zipCode }, propertyType?, tenantId?, loanId? }
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<{
    loanId: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    propertyType: string;
  }>({
    loanId: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: 'residential',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await propertyValuationsApi.create({
        address: {
          street: createForm.street,
          city: createForm.city,
          state: createForm.state,
          zipCode: createForm.zipCode,
        },
        propertyType: createForm.propertyType || undefined,
        loanId: createForm.loanId || undefined,
      });
      setShowCreateForm(false);
      setCreateForm({ loanId: '', street: '', city: '', state: '', zipCode: '', propertyType: 'residential' });
      void fetchValuations();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create valuation');
    } finally {
      setIsCreating(false);
    }
  };

  const propertyTypeOptions = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'land', label: 'Land' },
    { value: 'mixed_use', label: 'Mixed Use' },
  ];

  // -------------------------------------------------------------------------
  // Property type filter and address search (client-side, over the loaded
  // rows). A type option is a label, so the stored SINGLE_FAMILY and a form's
  // lower-case value that read the same are one choice.
  // -------------------------------------------------------------------------

  const filters = useMemo<FilterDefinition[]>(() => {
    const labels = new Set(book.map(typeLabel).filter((l): l is string => l != null));
    if (typeFilter) labels.add(typeFilter);
    if (labels.size === 0) return [STATUS_FILTER];
    const options = [...labels].sort().map((label) => ({ value: label, label }));
    return [STATUS_FILTER, { id: 'propertyType', label: 'Property type', type: 'select', options }];
  }, [book, typeFilter]);

  const visibleValuations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return valuations.filter(
      (v) =>
        (typeFilter == null || typeLabel(v) === typeFilter) &&
        (!query ||
          [v.address?.street, v.address?.city, v.address?.state, v.address?.zipCode]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(query))
    );
  }, [valuations, typeFilter, searchQuery]);

  // -------------------------------------------------------------------------
  // Summary metrics. Only reported figures enter a sum or an average; an
  // absent confidence is not a 0.
  // -------------------------------------------------------------------------

  const certified = book.filter((v) => v.status === 'CERTIFIED');
  const certifiedValue = sumReported(certified.map((v) => reportedNumber(v.certifiedValue)));
  const avgConfidence = meanReported(book.map((v) => reportedNumber(v.confidenceScore)));

  const totalTile = tile(read, String(book.length));
  const certifiedValueTile = tile(
    read,
    certifiedValue == null ? null : formatUsdCompact(certifiedValue),
    certified.length === 0 ? 'None certified' : METRIC_READ_CAPTIONS.notReported
  );
  const certifiedCountTile = tile(read, String(certified.length));
  const confidenceTile = tile(
    read,
    avgConfidence == null ? null : `${Math.round(avgConfidence * 100)}%`,
    book.length === 0 ? 'No valuations on file' : METRIC_READ_CAPTIONS.notReported
  );

  // -------------------------------------------------------------------------
  // Columns. Type appears only once a row reports one: the list endpoint
  // returns no property type for stored valuations today.
  // -------------------------------------------------------------------------

  const anyType = valuations.some((v) => reportedPropertyType(v.propertyType) != null);

  const columns = useMemo<ColumnDef<PropertyValuation, unknown>[]>(() => {
    const cols: ColumnDef<PropertyValuation, unknown>[] = [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (v) => v.address?.street ?? '',
        cell: ({ row }) => {
          const v = row.original;
          const href = profileHref(v);
          if (!v.address) return <Absent />;
          const lines = (
            <>
              <span className="text-sm font-sans font-medium text-primary leading-normal block">
                {v.address.street}
              </span>
              <span className="text-xs font-sans text-tertiary">
                {v.address.city}, {v.address.state} {v.address.zipCode}
              </span>
            </>
          );
          return (
            <div className="flex items-start gap-2">
              <MapPin aria-hidden="true" className="w-4 h-4 text-tertiary mt-0.5 flex-shrink-0" />
              {href ? (
                // The keyboard and screen-reader target for the row. The grid
                // leaves clicks on a button to the button, so this handler and
                // the row handler never both fire.
                <button
                  type="button"
                  onClick={() => navigate(href)}
                  aria-label={`Open property profile, ${v.address.street}, ${v.address.city}`}
                  className="text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection focus-visible:ring-offset-2"
                >
                  {lines}
                </button>
              ) : (
                <div>{lines}</div>
              )}
            </div>
          );
        },
        size: 280,
      },
    ];
    if (anyType) {
      cols.push({
        id: 'propertyType',
        header: 'Type',
        accessorFn: (v) => reportedPropertyType(v.propertyType) ?? '',
        cell: ({ row }) => {
          const type = reportedPropertyType(row.original.propertyType);
          return type == null ? (
            <Absent />
          ) : (
            <span className="text-sm font-sans text-secondary leading-normal">
              {labelFor(PROPERTY_TYPE_LABELS, type)}
            </span>
          );
        },
        size: 140,
      });
    }
    cols.push(
      {
        id: 'estimatedValue',
        header: () => <RightHeader>Estimated value</RightHeader>,
        accessorFn: (v) => reportedNumber(v.certifiedValue) ?? undefined,
        cell: ({ row }) => {
          const value = reportedNumber(row.original.certifiedValue);
          return (
            <span className="block text-right">
              {value == null ? (
                <Absent />
              ) : (
                <span className="text-sm font-mono font-semibold text-primary tabular-nums leading-normal">
                  {formatUsd(value)}
                </span>
              )}
            </span>
          );
        },
        size: 160,
      },
      {
        id: 'confidence',
        header: 'Confidence',
        accessorFn: (v) => reportedNumber(v.confidenceScore) ?? undefined,
        cell: ({ row }) => {
          const score = reportedNumber(row.original.confidenceScore);
          if (score == null) return <Absent />;
          const pct = Math.round(score * 100);
          return (
            <div className="inline-flex items-center gap-2">
              <div aria-hidden="true" className="w-16 h-2 bg-recessed rounded-full overflow-hidden">
                <div
                  data-testid="confidence-fill"
                  className="h-full rounded-full bg-secondary"
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                />
              </div>
              <span className="text-xs font-mono text-secondary tabular-nums">{pct}%</span>
            </div>
          );
        },
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (v) => v.status,
        cell: ({ row }) => <EnumBadge value={row.original.status} map={VALUATION_STATUS} />,
        size: 140,
      },
      {
        id: 'sources',
        header: 'Sources',
        accessorFn: (v) => (v.sources ?? []).join(', '),
        cell: ({ row }) => {
          const sources = row.original.sources ?? [];
          return sources.length > 0 ? (
            <span className="text-sm font-sans text-secondary leading-normal">{sources.join(', ')}</span>
          ) : (
            <Absent />
          );
        },
        size: 140,
      }
    );
    return cols;
  }, [anyType, navigate]);

  const filtered = statusFilter != null || typeFilter != null || searchQuery.trim() !== '';

  return (
    <div>
      <ListHeader
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Real estate valuations for collateral assessment
          </p>
        }
        onRefresh={() => void fetchValuations()}
      >
        <Button variant="primary" onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="w-4 h-4" aria-hidden="true" />
          New Valuation
        </Button>
      </ListHeader>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="mb-6 p-6">
          <Heading level={3} size="subsection" className="mb-4">New Property Valuation</Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Loan ID (optional)"
              placeholder="Leave blank for a valuation not tied to a loan"
              value={createForm.loanId}
              onChange={(e) => setCreateForm({ ...createForm, loanId: e.target.value })}
            />
            <Select
              label="Property Type"
              value={createForm.propertyType}
              onChange={(e) => setCreateForm({ ...createForm, propertyType: e.target.value })}
              options={propertyTypeOptions}
            />
            <Input
              label="Street Address"
              placeholder="123 Main St"
              value={createForm.street}
              onChange={(e) => setCreateForm({ ...createForm, street: e.target.value })}
              required
            />
            <Input
              label="City"
              placeholder="City"
              value={createForm.city}
              onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
              required
            />
            <Input
              label="State"
              placeholder="CA"
              value={createForm.state}
              onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
              required
            />
            <Input
              label="ZIP Code"
              placeholder="90210"
              value={createForm.zipCode}
              onChange={(e) => setCreateForm({ ...createForm, zipCode: e.target.value })}
              required
            />
          </div>
          {createError && (
            <p className="text-sm text-error mt-3">{createError}</p>
          )}
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-recessed">
            <Button variant="secondary" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => void handleCreate()}
              disabled={isCreating || !createForm.street || !createForm.city || !createForm.state || !createForm.zipCode}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </Card>
      )}

      {error && (
        <Alert type="error" title="Unable to load property valuations" className="mb-6">
          {error}
        </Alert>
      )}

      <MetricCardGrid className="mb-6">
        <MetricCard label="Total Valuations" {...totalTile} />
        <MetricCard label="Certified Value" {...certifiedValueTile} />
        <MetricCard label="Certified" {...certifiedCountTile} />
        <MetricCard label="Avg Confidence" {...confidenceTile} />
      </MetricCardGrid>

      <FilterBar
        filters={filters}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by address..."
        className="rounded-t-sm"
      />

      {read === 'loading' ? (
        <GridSkeleton />
      ) : read === 'failed' ? null : visibleValuations.length === 0 ? (
        <GridEmpty
          icon={<Home className="h-8 w-8" />}
          title={filtered ? 'No valuations match these filters' : 'No property valuations on file'}
          description={
            filtered
              ? 'Clear the filters or the search to see every valuation.'
              : // Revalue on a property profile is an Aaim-operator action (GH #678), so
                // the hint names only the control every reader has.
                'Use New Valuation to value a property.'
          }
        />
      ) : (
        <DataGrid<PropertyValuation>
          data={visibleValuations}
          columns={columns}
          onRowClick={(v) => {
            const href = profileHref(v);
            if (href) navigate(href);
          }}
          enablePagination
          defaultPageSize={25}
          enableColumnResize
          hoverable
          striped
          getRowId={(v) => v.valuationId}
          className="rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
